import { GeneralError } from '51trips-frontend-core'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import CheckIcon from '../../assets/icons/check.svg?react'
import { SendMagicLinkUseCase } from '../../domain/useCases/auth/SendMagicLinkUseCase'
import { Button } from '../Button'
import { AuthErrorBanner } from './AuthErrorBanner'

interface MagicLinkFormProps {
  sendMagicLinkUseCase: SendMagicLinkUseCase
  email: string
  onUsePasswordInstead: () => void
}

export const MagicLinkForm: React.FC<MagicLinkFormProps> = ({
  sendMagicLinkUseCase,
  email,
  onUsePasswordInstead
}) => {
  const { t } = useTranslation()

  const [isResending, setIsResending] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined)
  const [hasResent, setHasResent] = useState(false)

  const handleResend = () => {
    if (isResending) {
      return
    }

    setIsResending(true)
    setErrorMessage(undefined)

    sendMagicLinkUseCase.sendMagicLink(email).subscribe({
      next: () => {
        setIsResending(false)
        setHasResent(true)
      },
      error: (error: GeneralError) => {
        setErrorMessage(error.message || t('magicLink.sendError', { ns: 'auth' }))
        setIsResending(false)
      }
    })
  }

  return (
    <div className="flex flex-col items-center gap-5 text-center">
      <span className="size-[46px] rounded-full bg-neutralBlack-100 flex items-center justify-center">
        <CheckIcon className="size-5 text-neutralBlack-900" />
      </span>

      <div className="flex flex-col gap-2">
        <h1 className="text-[20px] font-semibold tracking-tight text-neutralBlack-900">
          {t('magicLink.sentTitle', { ns: 'auth' })}
        </h1>
        <p className="text-[13.5px] leading-snug text-neutralBlack-500">
          {t('magicLink.sentDescription', { ns: 'auth', email })}
        </p>
      </div>

      {errorMessage ? <AuthErrorBanner message={errorMessage} /> : null}
      {hasResent && !errorMessage ? (
        <p className="text-xs text-neutralBlack-400">{t('magicLink.resent', { ns: 'auth' })}</p>
      ) : null}

      <div className="flex flex-col gap-2.5 w-full">
        <Button
          variant="outlined"
          size="large"
          label={t('magicLink.resend', { ns: 'auth' })}
          onClick={handleResend}
          disabled={isResending}
          block
        />

        <button
          type="button"
          onClick={onUsePasswordInstead}
          className="text-xs text-neutralBlack-500 hover:text-neutralBlack-900 cursor-pointer"
        >
          {t('magicLink.usePasswordInstead', { ns: 'auth' })}
        </button>
      </div>
    </div>
  )
}
